import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  Inject,
  Renderer2,
  ElementRef,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { FADE_IN_OUT_ANIMATION_SLOWER, SCROLLOPACITY, SCROLLFOLDPERSON } from '../../../../core/animations/';
import { Cart, Product, ProductCategory } from '../../services/entities';
import { arrowLeft, chevronDown, edit, IconComponent, provideIcons, reply } from '../../../../shared/icons';
import { forkJoin, startWith } from 'rxjs';
import { Shop } from '../../../../shared/entities/shop';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from '../../services/product/product.service';
import { ResolutionService } from '../../../../core/services/resolution.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { ScrollService } from '../../../../core/services/scroll.service';
import { ShopInfoService } from '../../../../core/services/shop-info.service';
import { AlpitranslatePipe } from '../../../../core/pipes/alpitranslate.pipe';
import { ProductListCategoriesComponent } from '../product-list-categories/product-list-categories.component';
import { ProductListComponent } from '../product-list/product-list.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton-loader/skeleton-loader.component';
import { CartEventType, CartService } from '../../services/cart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgeCategoriesInterface } from '../../../../shared/interfaces/age-categories-interface';
import { AgeIntervalInterface } from '../../../../shared/interfaces/age-interval-interface';
import { FilterProductsByPipe, ProductCategoriesPipe } from '../../pipes';

@Component({
  selector: 'app-product-list-modal',
  templateUrl: './product-list-modal.component.html',
  styleUrls: ['./product-list-modal.component.scss'],
  animations: [FADE_IN_OUT_ANIMATION_SLOWER, SCROLLFOLDPERSON, SCROLLOPACITY],
  imports: [
    IconComponent,
    AlpitranslatePipe,
    ProductListCategoriesComponent,
    ProductListComponent,
    RouterModule,
    SkeletonLoaderComponent,
    FilterProductsByPipe,
    ProductCategoriesPipe
  ],
  providers: [
    provideIcons({
      reply,
      chevronDown,
      edit,
      arrowLeft
    })
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListModalComponent implements OnInit, OnDestroy {
  @HostBinding('@fadeInOut') fadeInOut = 'in';
  shopId = this.route.snapshot.queryParams['shopId'];

  protected shop: Shop;
  cart: Cart;
  selectedCategory: ProductCategory;
  products: Product[];
  ageCategories: AgeCategoriesInterface[] = [];
  ageIntervals: Map<number, Array<AgeIntervalInterface>> = new Map<number, Array<AgeIntervalInterface>>();

  constructor(
    public translateService: TranslateService,
    private productService: ProductService,
    private resolutionService: ResolutionService,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private renderer2: Renderer2,
    public scrollService: ScrollService,
    private root: ElementRef,
    protected shopInfoService: ShopInfoService,
    private cartService: CartService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (!this.shopId) {
      this.router.navigate(['/']);
    } else {
      if (this.document) {
        this.renderer2.setStyle(this.document.body, 'overflow', 'hidden');
      }

      this.cartService.latestProductsWithDiscounts$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.cdr.markForCheck();
        });

      this.cartService.cart$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        this.cart = event.cart;

        if (this.cart.selectedPerson.productEntities.length) {
          const categories = this.cart.selectedPerson.productEntities.map(p => p.productCategoryId);
          if (!categories.includes(this.selectedCategory)) {
            this.selectedCategory = categories[0];
          }
        }

        if (event.eventType === CartEventType.productUpdated) {
          this.close();
        }

        this.cdr.markForCheck();
      });
      this.initShopInfo(this.shopId);
    }

    this.resolutionService.sizeReferenceChange
      .pipe(startWith(''), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.resolutionService.isLargeDevice()) {
          this.close(true);
        }
      });

    this.scrollToSelectedProduct();
  }

  selectedPersonIndex(): number {
    return this.cart.persons.indexOf(this.cart.selectedPerson);
  }

  initShopInfo(shopId: string) {
    forkJoin([this.shopInfoService.getLocationById(shopId), this.productService.getProducts(+shopId)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([shop, products]) => {
          this.products = products.products;
          this.shop = shop;
          shop.ageIntervals.forEach(interval => {
            if (!this.ageCategories.find(ac => ac.id === interval.age_category.id)) {
              this.ageCategories.push(interval.age_category);
            }
          });
          this.ageCategories.sort((a, b) => {
            if (a.rank > b.rank) {
              return 1;
            }
            if (a.rank < b.rank) {
              return -1;
            }
            return 0;
          });
          this.ageCategories.forEach(ageCat => {
            this.ageIntervals.set(
              ageCat.id,
              shop.ageIntervals.filter(ageInterval => ageInterval.age_category_id === ageCat.id)
            );
          });
        }
      });
  }

  changeAge(ageIntervalId: number) {
    const ageInterval = this.shop.ageIntervals.find(ai => ai.id === ageIntervalId);
    this.cartService.updatePersonAge(ageInterval, this.selectedPersonIndex());
  }

  getAgeCategoryLabel(category: AgeCategoriesInterface, lang: string): string {
    const cattrans = category.translations.find(trans => trans.lang === lang);
    return cattrans ? cattrans.name : '';
  }

  updateName($event) {
    this.cartService.updatePersonName($event.target.value, this.selectedPersonIndex());
  }

  ngOnDestroy() {
    if (this.document) {
      this.renderer2.setStyle(this.document.body, 'overflow', 'initial');
    }
  }

  /**
   * will do stuff on selecting the input field.
   * @param $event the input click event
   */
  focusedName($event) {
    $event.target.select();
  }

  /**
   * closes the modal hierachically. If model modal is also open, it will only close this
   * you can close both with the closeAll param -> true. (default: false)
   * @param closeAll
   */
  close(closeAll = false) {
    if (this.route.firstChild && !closeAll) {
      // close the model modal
      this.router.navigate([{ outlets: { sideBar: null } }], {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      });
    } else if (this.route.firstChild && closeAll) {
      // close on resize but reopen model modal
      const productId = this.route.firstChild.snapshot.params.productId;
      this.router.navigate([{ outlets: { modal: null, sideBar: null } }], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
        skipLocationChange: true
      });
      // in timeout to prevent confusion of the router-outlet sind "sideBar" exists globally and on this component
      setTimeout(() => {
        this.router.navigate([{ outlets: { modal: null, sideBar: productId } }], {
          relativeTo: this.route.parent,
          queryParamsHandling: 'merge',
          skipLocationChange: true
        });
      });
    } else {
      // close both
      this.router.navigate([{ outlets: { modal: null, sideBar: null } }], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge'
      });
    }
  }

  /**
   * some shops contain the SAME ski length but for different ages.
   * We need to differentiate them in the interval label with this flag
   * @param id the category with bad bad identical intervals
   */
  categoryHasIdenticalIntervals(id: number): boolean {
    const intervals = this.ageIntervals.get(id);

    if (intervals.length > 1) {
      for (const interval of intervals) {
        if (
          intervals.filter(
            _int => _int.height_start === interval.height_start && _int.height_end === interval.height_end
          ).length > 1
        ) {
          return true;
        }
      }
    }
    return false;
  }

  numberOfIntervals() {
    let count = 0;
    for (const cat of this.ageCategories) {
      count += this.ageIntervals.get(cat.id).length;
    }
    return count;
  }

  /**
   * Scrolls to the product in the list
   */
  scrollToSelectedProduct() {
    if (this.cart?.selectedPerson?.products.length) {
      const element = this.document.getElementById(
        'product-scroll-anchor-' + this.cart.selectedPerson.products[0].definitionId
      );
      if (element) {
        this.scrollService.scrollIntoView(element, this.root.nativeElement, 35);
      }
    }
  }

  scrollUp() {
    this.scrollService.scrollTo(0, this.root.nativeElement, 0);
  }
}
